import { useEffect, useState } from 'preact/hooks';
import { DashboardApi, HttpError, type InvitePreview } from '../api.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

const ROLE_LABEL: Record<InvitePreview['role'], string> = {
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

export function AcceptInvite({ token }: { token: string }): preact.ComponentChildren {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'preview'; preview: InvitePreview }
    | { kind: 'accepted'; orgId: string; role: string }
    | { kind: 'error'; message: string; code: string }
  >({ kind: 'loading' });
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const preview = await api.previewInvite(token);
        setState({ kind: 'preview', preview });
      } catch (e) {
        if (e instanceof HttpError) {
          setState({ kind: 'error', message: e.message, code: e.code });
        } else {
          setState({ kind: 'error', message: String(e), code: 'unknown' });
        }
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const r = await api.acceptInvite(token);
      // No auto-redirect (WCAG 2.2.1 Timing Adjustable) — the confirmation
      // below offers an explicit "Continue to dashboard" link instead.
      setState({ kind: 'accepted', orgId: r.orgId, role: r.role });
    } catch (e) {
      if (e instanceof HttpError) {
        if (e.status === 401) {
          // Not signed in — redirect to login, then back here.
          const next = encodeURIComponent(window.location.pathname);
          window.location.href = `/?returnTo=${next}`;
          return;
        }
        setState({ kind: 'error', message: e.message, code: e.code });
      } else {
        setState({ kind: 'error', message: String(e), code: 'unknown' });
      }
    } finally {
      setAccepting(false);
    }
  };

  return (
    <main style={{ padding: 48, maxWidth: 560, margin: '0 auto' }}>
      <h1>Invitation</h1>
      {state.kind === 'loading' && (
        <p class="muted">
          <output>Looking up invitation…</output>
        </p>
      )}
      {state.kind === 'error' && (
        <Banner variant="warn">
          <strong>{state.message}</strong>
          {state.code === 'invite_invalid' && (
            <p>This invitation has expired, been revoked, or already been used.</p>
          )}
          {state.code === 'invite_email_mismatch' && (
            <p>Sign in with the email address the invitation was sent to.</p>
          )}
        </Banner>
      )}
      {state.kind === 'preview' && (
        <>
          <p>
            You've been invited to join <strong>{state.preview.orgName}</strong> as{' '}
            <strong>{ROLE_LABEL[state.preview.role]}</strong>.
          </p>
          <p class="small muted">
            Invitation sent to <code>{state.preview.email}</code>. Expires{' '}
            {new Date(state.preview.expiresAt).toLocaleString()}.
          </p>
          <button type="button" disabled={accepting} onClick={() => void accept()}>
            {accepting ? 'Accepting…' : 'Accept invitation'}
          </button>
        </>
      )}
      {state.kind === 'accepted' && (
        <Banner>
          You're in — your invitation was accepted. <a href="/">Continue to dashboard</a>
        </Banner>
      )}
    </main>
  );
}
